<template>
  <section class="ore-product-section" v-if="currentOreData && currentOreData.length > 0">
    <div class="container">
      <!-- 上方：详细介绍区域 -->
      <div class="active-ore-display animate__animated animate__fadeIn" :key="activeIndex">
        <div class="ore-detail-card">
          <!-- 左侧/上方：单质矿展示 (移动端支持左右滑动) -->
          <div class="pure-list-container">
            <div class="pure-scroll-wrapper">
              <div v-for="(pure, pIdx) in activeOre.pure_list" :key="pIdx" class="pure-item">
                <div class="pure-image-box">
                  <el-image :src="pure.pure_image" fit="cover">
                    <template #error>
                      <div class="image-placeholder">
                        <span>{{ pure.pure_name }}</span>
                      </div>
                    </template>
                  </el-image>
                </div>
                <div class="pure-desc-box">
                  <h4>{{ pure.pure_name }}</h4>
                  <p>{{ pure.description }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧/下方：企业技术与产量介绍 -->
          <div class="ore-enterprise-content">
            <h2 class="ore-title">{{ activeOre.name }}</h2>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-header">
                  <h4>{{ t('ore.tech_title') || '冶炼技术' }}</h4>
                </div>
                <p>{{ activeOre.tech_intro }}</p>
              </div>
              <div class="info-item">
                <div class="info-header">
                  <h4>{{ t('ore.output_title') || '产量概况' }}</h4>
                </div>
                <p>{{ activeOre.production_intro }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部：矿石列表选择器 (自适应多行居中) -->
      <div class="ore-list-wrapper">
        <div
          v-for="(item, index) in currentOreData"
          :key="index"
          class="ore-thumb-item"
          :class="{ 'active': activeIndex === index }"
          @mouseenter="activeIndex = index"
          @click="activeIndex = index"
        >
          <div class="thumb-img-box">
            <el-image :src="item.raw_image" fit="cover">
              <template #error>
                <div class="image-placeholder">
                  <span>{{ item.name }}</span>
                </div>
              </template>
            </el-image>
          </div>
          <span class="thumb-name">{{ item.name }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();
const activeIndex = ref(0);

watch(locale, () => {
  activeIndex.value = 0;
});

const oreDataConfigs = {
  cn: [
    {
      name: '铜金矿图片占位',
      raw_image: '',
      tech_intro: '采用XXX工艺，实现铜金的高效分离，金回收率达XX%以上。',
      production_intro: '年产电解铜XX万吨，黄金X吨。',
      pure_list: [
        { pure_name: '产品1图片占位', pure_image: '', description: '产品1描述（包含用途、特性等）' },
        { pure_name: '产品2图片占位', pure_image: '', description: '产品2描述（包含用途、特性等）' }
      ]
    },
    {
      name: '硫铁矿图片占位',
      raw_image: '',
      tech_intro: '利用XXXXXX工艺实现绿色循环生产。',
      production_intro: '硫精矿年产能达XXXX吨。',
      pure_list: [
        { pure_name: '产品1图片占位', pure_image: '', description: '产品1描述（包含用途、特性等）' },
        { pure_name: '产品2图片占位', pure_image: '', description: '产品2描述（包含用途、特性等）' }
      ]
    },
    {
      name: '铜钼矿图片占位',
      raw_image: '',
      tech_intro: '应用XXXX技术，有效解决低品位共生矿的分离难题。',
      production_intro: '年产钼精矿（X% Mo）约XXX吨。',
      pure_list: [
        { pure_name: '产品1图片占位', pure_image: '', description: '产品1描述（包含用途、特性等）' },
        { pure_name: '产品2图片占位', pure_image: '', description: '产品2描述（包含用途、特性等）' }
      ]
    }
    // 预留更多矿石位置，最多支持8个
  ],
  tc: [ /* 繁体数据同上逻辑 */],
  en: [ /* 英文数据同上逻辑 */]
};

const currentOreData = computed(() => oreDataConfigs[locale.value] || oreDataConfigs.cn);
const activeOre = computed(() => currentOreData.value[activeIndex.value] || {});
</script>
<style lang="scss" scoped>
@import "@/assets/css/common";

.ore-product-section {
  padding: 60px 0;
  background-color: #f8f9fa;
  min-height: 100vh; // 尽量保证一屏感
  display: flex;
  align-items: center;

  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  // 上方展示卡片
  .active-ore-display {
    margin-bottom: 40px;

    .ore-detail-card {
      display: flex;
      background: #fff;
      border-radius: 20px;
      box-shadow: 0 15px 40px rgba(0, 0, 0, 0.06);
      overflow: hidden;
      min-height: 480px;

      @media (max-width: 992px) {
        flex-direction: column;
        min-height: auto;
      }
    }

    // 单质矿列表 (核心优化：滚动支持)
    .pure-list-container {
      flex: 1.2;
      background: #fafafa;
      padding: 30px;
      border-right: 1px solid #eee;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: center;
      @media (max-width: 992px) {
        border-right: none;
        border-bottom: 1px solid #eee;
        padding: 20px;
      }

      .pure-scroll-wrapper {
        display: flex;
        flex-direction: column;
        gap: 20px;

        @media (max-width: 992px) {
          flex-direction: row; // 移动端改为横向
          overflow-x: auto;
          padding-bottom: 10px;
          -webkit-overflow-scrolling: touch;

          &::-webkit-scrollbar {
            height: 4px;
          }
          &::-webkit-scrollbar-thumb {
            background: #ddd;
            border-radius: 10px;
          }
        }
      }

      .pure-item {
        display: flex;
        gap: 20px;
        background: #fff;
        padding: 15px;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.03);
        flex-shrink: 0;

        @media (max-width: 992px) {
          width: 280px; // 移动端固定宽度以便滑动
        }

        .pure-image-box {
          width: 80px;
          height: 80px;
          flex-shrink: 0;
          border-radius: 8px;
          overflow: hidden;
          background: #f0f2f5;
        }

        .pure-desc-box {
          h4 {
            margin: 0 0 6px 0;
            font-size: 17px;
            color: #2c3e50;
          }
          p {
            margin: 0;
            font-size: 13px;
            color: #7f8c8d;
            line-height: 1.4;
          }
        }
      }
    }

    // 企业介绍区域
    .ore-enterprise-content {
      flex: 1;
      padding: 40px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      @media (max-width: 992px) {
        padding: 30px 20px;
      }

      .ore-title {
        font-size: 28px;
        color: #2c3e50;
        margin-bottom: 25px;
        font-weight: 700;
        border-left: 5px solid $primary-color;
        padding-left: 15px;
      }

      .info-grid {
        display: flex;
        flex-direction: column;
        gap: 25px;

        .info-item {
          h4 {
            font-size: 16px;
            color: #34495e;
            margin: 0 0 10px 0;
            font-weight: 600;
            position: relative;
            display: inline-block;

            &::after {
              content: '';
              position: absolute;
              bottom: -4px;
              left: 0;
              width: 100%;
              height: 2px;
              background: rgba($primary-color, 0.2);
            }
          }
          p {
            font-size: 14px;
            color: #606266;
            line-height: 1.6;
            margin: 0;
          }
        }
      }
    }
  }

  // 底部列表选择器 (核心优化：多行居中)
  .ore-list-wrapper {
    display: flex;
    justify-content: center;
    flex-wrap: wrap; // 支持换行
    gap: 20px;
    padding: 10px 0;

    @media (max-width: 768px) {
      display: grid;
      grid-template-columns: repeat(4, 1fr); // 移动端每行4个
      gap: 15px;
      justify-items: center;
    }

    .ore-thumb-item {
      cursor: pointer;
      text-align: center;
      transition: all 0.3s ease;
      width: 120px; // 缩小尺寸以适应更多矿石

      @media (max-width: 768px) {
        width: 70px; // 移动端进一步缩小
      }

      .thumb-img-box {
        width: 100%;
        aspect-ratio: 1/1; // 保持正方形
        border-radius: 12px;
        overflow: hidden;
        border: 2px solid transparent;
        margin-bottom: 8px;
        background: #fff;
        box-shadow: 0 4px 10px rgba(0,0,0,0.06);
        transition: all 0.3s ease;

        .el-image {
          width: 100%;
          height: 100%;
          transition: transform 0.3s ease;
        }
      }

      .thumb-name {
        font-size: 14px;
        color: #909399;
        transition: all 0.3s;
        white-space: nowrap;

        @media (max-width: 768px) {
          font-size: 12px;
        }
      }

      // 核心优化：Active 状态使用 Scale
      &:hover, &.active {
        .thumb-img-box {
          border-color: $primary-color;
          transform: scale(1.1); // 缩放而非位移
          box-shadow: 0 8px 20px rgba($primary-color, 0.2);
        }

        .thumb-name {
          color: $primary-color;
          font-weight: 600;
          transform: scale(1.05);
        }
      }
    }
  }

  .image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f2f5;
    color: #909399;
    font-size: 12px;
    text-align: center;
  }
}
</style>